import { useState } from 'react'
import { toast } from 'sonner'
import { useProjectStore } from '@/hooks/use-project-store'
import { usePreviewStore } from '@/hooks/use-preview-store'
import { useCurrentUser } from '@/hooks/use-current-user'
import sendToBothAnalytics from '@/lib/mixpanel/sendToBothAnalytics'
import { useTranslations } from 'next-intl'
import { usePlayConfetti } from '@/contexts/confetti'
import { pushModal } from '@/components/modals'
import { MIXPANEL_CLIENT_EVENT_NAMES } from '@/lib/mixpanel/constant'

interface IPublishOptions {
  successToast?: boolean
  onSuccess?: () => void
  needPlayConfetti?: boolean
  needShowPublishResult?: boolean
}

/**
 * 发布当前项目，使用默认参数
 */
export function useProjectPublish() {
  const [isPublishing, setIsPublishing] = useState(false)
  const { project, getProject, setProject } = useProjectStore()
  const { updateScreenshot, updateOpenGraphImage, uploadBuildDist2R2 } =
    usePreviewStore()
  const { data: user } = useCurrentUser()
  const t = useTranslations('project-preview')
  const { show: playConfetti } = usePlayConfetti()

  const publish = async ({
    onSuccess,
    successToast = false,
    needPlayConfetti = false,
    needShowPublishResult = false,
  }: IPublishOptions = {}) => {
    if (!project) {
      toast.error('Project not found')
      return
    }

    setIsPublishing(true)
    try {
      // if thumbnail is null, update screenshot in background (don't block publish)
      if (!getProject()?.thumbnail) {
        updateScreenshot() // async, no await - runs in background
      }

      const updatedProject = getProject()

      if (!updatedProject) {
        toast.error('Project not found')
        return
      }

      // 再上传构建产物
      await uploadBuildDist2R2(updatedProject)

      // 再更新项目状态为 live
      const formData = new FormData()
      formData.append('projectId', updatedProject._id.toString())
      formData.append('status', 'live')
      formData.append(
        'buildUrl',
        updatedProject.build_url || updatedProject.deploy_url || '',
      )
      const statusResponse = await fetch('/api/me/project', {
        method: 'POST',
        body: formData,
      })
      if (!statusResponse.ok) {
        throw new Error('Failed to update project status')
      }

      // 为了拿到最新的应用
      const currentProject = getProject()
      if (currentProject) {
        setProject({
          ...currentProject,
          status: 'live',
        })
      }

      sendToBothAnalytics(
        MIXPANEL_CLIENT_EVENT_NAMES.PUBLISH_PROJECT,
        {
          project_id: updatedProject._id.toString(),
          project_name: updatedProject.project_name,
          visibility: updatedProject.visibility,
          custom_domain: updatedProject.custom_domain,
        },
        user?._id.toString(),
      )

      onSuccess?.()

      if (successToast) {
        toast.success(t('publish_success'))
      }

      if (needPlayConfetti) {
        playConfetti(3e3)
      }

      if (needShowPublishResult) {
        pushModal('PublishResultModal', {
          buildUrl: getProject()?.build_url || getProject()?.deploy_url!,
        })
      }

      return getProject()?.deploy_url
    } catch (error: any) {
      console.error('Deployment failed:', error)
      toast.error(`Deployment failed: ${error.message}`)
    } finally {
      setIsPublishing(false)
    }
  }

  return {
    publish,
    isPublishing,
  }
}
